import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FASAuthService } from 'fas-auth-poc';
import { MessageService } from 'primeng/api';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  INTERNAL_EXAM_SESSION_MODE_VALUES,
  InternalExamDeviceVm,
  InternalExamLaunchTarget,
  InternalExamSessionMode,
} from './internal-exam.models';
import { InternalExamService } from './internal-exam.service';
import {
  STATION_HANDOFF_ROUTE,
  storeStationHandoffLink,
} from './station-handoff.storage';
import { isOskBoss } from '../../../shared/helpers/osk-staff-role';
import { centerDynamicModalConfig } from '../../../shared/helpers/modal.config';
import {
  LectureAttendanceDialogComponent,
  LectureAttendanceDialogData,
} from '../../lecture-groups/lecture-attendance/lecture-attendance-dialog.component';

@Component({
  selector: 'app-internal-exam-dialog',
  templateUrl: './internal-exam-dialog.component.html',
  styleUrl: './internal-exam-dialog.component.scss',
})
export class InternalExamDialogComponent implements OnInit, OnDestroy {
  private readonly internalExamService = inject(InternalExamService);
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly fasAuthService = inject(FASAuthService);
  private readonly messageService = inject(MessageService);
  private readonly primengDialogService = inject(DialogService);

  readonly studentGuid = this.dialogConfig.data.studentGuid as string | undefined;
  /** Set instead of the student when the lectures are held for a whole lecture group. */
  readonly lectureGroupGuid = this.dialogConfig.data.lectureGroupGuid as
    | string
    | undefined;
  readonly lectureGroupName = this.dialogConfig.data.lectureGroupName as
    | string
    | undefined;
  readonly studentDisplayName = this.dialogConfig.data
    .studentDisplayName as string;
  /** What the student will do on the station - the same dialog starts exams and lectures. */
  readonly sessionMode: InternalExamSessionMode = this.lectureGroupGuid
    ? 'lectures'
    : (this.dialogConfig.data.mode as InternalExamSessionMode | undefined) ??
      'exam';
  readonly sessionLabel =
    this.sessionMode === 'lectures' ? 'wykłady' : 'egzamin wewnętrzny';
  /**
   * How a computer becomes a station: pairing with the code it shows, in "Ustawienia OSK → Stanowiska",
   * which only the boss can open - employees are sent to the boss.
   */
  readonly stationSetupHint = isOskBoss()
    ? `Aby dodać komputer, otwórz na nim ${environment.internalExamUrl.replace(
        /\/+$/,
        ''
      )}, wybierz „Zaloguj jako OSK” i wpisz pokazany kod w Ustawieniach OSK → Stanowiska.`
    : 'Komputery dodaje właściciel OSK w Ustawieniach OSK → Stanowiska.';
  /** Hint next to "Inny komputer". */
  readonly otherComputerHint = `Uruchom ${this.sessionLabel} na stanowisku – komputerze dla kursantów w ośrodku. ${this.stationSetupHint}`;
  /**
   * Shown under "Ten komputer": the panel signs out here and the link into the station is shown to
   * copy into another browser (see StationHandoffPageComponent), so the choice is not a surprise.
   */
  readonly thisComputerNote = `Zostaniesz wylogowany z panelu na tym komputerze. Pokażemy link do skopiowania do innej przeglądarki, który uruchomi ${this.sessionLabel} – ważny 15 minut.`;
  /**
   * The signed-out page cannot tell the boss from an employee any more, so what to do with the
   * pairing code of a browser that is not a station yet is decided here.
   */
  private readonly pairingHint = isOskBoss()
    ? 'Jeśli ta przeglądarka nie jest jeszcze sparowana jako stanowisko, po otwarciu linku zobaczysz kod parowania – wpisz go po zalogowaniu do panelu w Ustawieniach OSK → Stanowiska. Link czeka do upływu ważności.'
    : 'Jeśli ta przeglądarka nie jest jeszcze sparowana jako stanowisko, po otwarciu linku zobaczysz kod parowania – zatwierdza go właściciel OSK w Ustawieniach OSK → Stanowiska. Link czeka do upływu ważności.';
  private readonly launchTarget: InternalExamLaunchTarget = this.lectureGroupGuid
    ? { lectureGroupGuid: this.lectureGroupGuid }
    : { studentGuid: this.studentGuid };

  mode: 'this-computer' | 'other-computer' = 'other-computer';
  /** Station setup (names, pairing) stays with the boss; instructors and lecturers only pick a station. */
  readonly canRenameDevices = isOskBoss();
  devices: InternalExamDeviceVm[] = [];
  selectedDeviceGuid: string | null = null;
  editingDeviceGuid: string | null = null;
  editingDeviceName = '';
  devicePendingSessionEnd: InternalExamDeviceVm | null = null;
  loading = true;
  submitting = false;
  renaming = false;
  endingSession = false;

  ngOnInit(): void {
    this.refreshDevices();
    void this.internalExamService
      .connectPresence(() => this.refreshDevices())
      .catch(() => {
        this.messageService.add({
          severity: 'warn',
          detail:
            'Nie udało się uruchomić podglądu online. Lista stanowisk może być nieaktualna.',
        });
      });
  }

  ngOnDestroy(): void {
    void this.internalExamService.disconnectPresence();
  }

  choose(): void {
    if (this.mode === 'this-computer') {
      this.startOnThisComputer();
      return;
    }

    if (!this.selectedDeviceGuid) {
      return;
    }

    const selectedDevice = this.devices.find(
      (device) => device.guid === this.selectedDeviceGuid
    );
    if (!selectedDevice?.online || selectedDevice.hasActiveSession) {
      this.selectedDeviceGuid = null;
      return;
    }

    this.submitting = true;
    this.internalExamService
      .launchOnDevice(this.launchTarget, this.selectedDeviceGuid, this.sessionMode)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            detail: this.lectureGroupGuid
              ? `Wykłady grupy ${this.lectureGroupName} zostały wysłane na wybrane stanowisko.`
              : this.sessionMode === 'lectures'
                ? `Wykłady kursanta ${this.studentDisplayName} zostały wysłane na wybrane stanowisko.`
                : `Egzamin kursanta ${this.studentDisplayName} został wysłany na wybrane stanowisko.`,
          });
          this.dialogRef.close(true);
          // Group lectures start with the attendance list: the station shows the QR code, the panel
          // shows who confirmed and lets the lecturer mark the others.
          if (this.lectureGroupGuid) {
            this.primengDialogService.open(LectureAttendanceDialogComponent, {
              ...centerDynamicModalConfig,
              width: '980px',
              data: {
                lectureGroupGuid: this.lectureGroupGuid,
                lectureGroupName: this.lectureGroupName ?? this.studentDisplayName,
                openToday: true,
              } satisfies LectureAttendanceDialogData,
            });
          }
        },
        error: (error) => this.showError(error),
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  deviceStatus(device: InternalExamDeviceVm): string {
    if (device.hasActiveSession) {
      return device.activeSessionMode ===
        INTERNAL_EXAM_SESSION_MODE_VALUES.lectures
        ? 'zajęte – wykłady'
        : 'zajęte – egzamin';
    }
    return device.online ? 'online' : 'offline';
  }

  startRenaming(device: InternalExamDeviceVm): void {
    this.editingDeviceGuid = device.guid;
    this.editingDeviceName = device.name;
  }

  cancelRenaming(): void {
    if (this.renaming) {
      return;
    }

    this.editingDeviceGuid = null;
    this.editingDeviceName = '';
  }

  requestSessionEnd(device: InternalExamDeviceVm): void {
    this.devicePendingSessionEnd = device;
  }

  cancelSessionEnd(): void {
    if (!this.endingSession) {
      this.devicePendingSessionEnd = null;
    }
  }

  confirmSessionEnd(): void {
    const device = this.devicePendingSessionEnd;
    if (!device || this.endingSession) {
      return;
    }

    this.endingSession = true;
    this.internalExamService
      .endDeviceSession(device.guid)
      .pipe(finalize(() => (this.endingSession = false)))
      .subscribe({
        next: () => {
          if (this.selectedDeviceGuid === device.guid) {
            this.selectedDeviceGuid = null;
          }
          this.devicePendingSessionEnd = null;
          this.messageService.add({
            severity: 'success',
            detail: `Aktywna sesja na stanowisku ${device.name} została zakończona.`,
          });
          this.refreshDevices();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.devicePendingSessionEnd = null;
            this.refreshDevices();
          }
          this.showError(error);
        },
      });
  }

  saveDeviceName(device: InternalExamDeviceVm): void {
    const name = this.editingDeviceName.trim();
    if (!name || this.renaming) {
      return;
    }

    this.renaming = true;
    this.internalExamService
      .renameDevice(device.guid, name)
      .pipe(finalize(() => (this.renaming = false)))
      .subscribe({
        next: (updatedDevice) => {
          this.devices = this.devices.map((item) =>
            item.guid === updatedDevice.guid ? updatedDevice : item
          );
          this.editingDeviceGuid = null;
          this.editingDeviceName = '';
          this.messageService.add({
            severity: 'success',
            detail: 'Nazwa stanowiska została zmieniona.',
          });
        },
        error: (error) => this.showError(error),
      });
  }

  private refreshDevices(): void {
    this.internalExamService
      .getDevices()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (devices) => {
          this.devices = devices;
          if (
            this.selectedDeviceGuid &&
            !devices.some(
              (device) =>
                device.guid === this.selectedDeviceGuid &&
                device.online &&
                !device.hasActiveSession
            )
          ) {
            this.selectedDeviceGuid = null;
          }
        },
        error: (error) => this.showError(error),
      });
  }

  /**
   * Starts the student on this computer without leaving the OSK panel behind: the single-use
   * handoff code is turned into a link to the exam application, the panel is signed out here for
   * good and the signed-out page shows the link to copy into another browser - never the OSK token.
   * The exam station authenticates itself with its own long lived terminal token, and shows a
   * pairing code when it has not been registered as an exam station yet.
   */
  private startOnThisComputer(): void {
    if (!this.fasAuthService.getToken()) {
      this.messageService.add({
        severity: 'error',
        detail:
          'Sesja OSK wygasła. Zaloguj się ponownie przed uruchomieniem egzaminu.',
      });
      return;
    }

    this.submitting = true;
    this.internalExamService
      .createHandoff(this.launchTarget, this.sessionMode)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (handoff) => {
          const handoffParams = new URLSearchParams({
            handoff: handoff.handoffCode,
          });
          const internalExamUrl = environment.internalExamUrl.replace(
            /\/+$/,
            ''
          );
          storeStationHandoffLink({
            url: `${internalExamUrl}/#${handoffParams.toString()}`,
            expiresAtUtc: handoff.expiresAtUtc,
            mode: this.sessionMode,
            subjectLabel: this.lectureGroupGuid ? 'Grupa' : 'Kursant',
            subjectName: this.lectureGroupGuid
              ? this.lectureGroupName ?? this.studentDisplayName
              : this.studentDisplayName,
            pairingHint: this.pairingHint,
          });

          this.dialogRef.close();
          this.fasAuthService.logout();
          localStorage.removeItem('authTokenExpired');
          // A full page load, not a router navigation: nothing of the signed-in panel stays in
          // memory and the panel's page leaves the browser history.
          window.location.replace(
            new URL(STATION_HANDOFF_ROUTE, document.baseURI).toString()
          );
        },
        error: (error) => this.showError(error),
      });
  }

  private showError(error: HttpErrorResponse): void {
    const apiMessage =
      typeof error.error === 'string'
        ? error.error
        : error.error?.message ?? error.error?.title;
    const detail = apiMessage?.includes(
      'already has an active internal exam session'
    )
      ? this.lectureGroupGuid
        ? 'Wybrane stanowisko ma już aktywną sesję. Zakończ poprzednią sesję i spróbuj ponownie.'
        : 'Kursant lub wybrane stanowisko ma już aktywną sesję egzaminu wewnętrznego. Zakończ poprzednią sesję i spróbuj ponownie.'
      : apiMessage?.includes('has no active internal exam session')
        ? 'Ta sesja została już zakończona. Odśwież listę stanowisk.'
        : apiMessage?.includes('archived group')
          ? 'Nie można rozpocząć wykładów dla zarchiwizowanej grupy.'
          : apiMessage ??
            `Nie udało się uruchomić (${this.sessionLabel}). Spróbuj ponownie.`;
    this.messageService.add({ severity: 'error', detail });
  }
}
